import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Pagination, { usePagination } from '../../../components/ui/Pagination';
import EmptyState from '../../../components/ui/EmptyState';
import { TableSkeleton } from '../../../components/ui/Skeleton';
import SortableTh, { useSort } from '../../../components/ui/SortableTh';
import useTableFilters from '../../../components/ui/useTableFilters';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';
import { adminService } from '../../../services/adminService';
import { formatDate } from '../../../utils/formatters';
import { conferenceStatusOptions } from '../../../constants/conferenceStatus';

const ConferenceManagement = () => {
  /* State first. Everything below derives from it, and a `const`
     binding from useState is unreachable until its own line runs. */
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Table-local sorting.
  const { sort, toggle, sortRows } = useSort('startDate', 'desc');
  const f = useTableFilters(conferences, React.useMemo(() => ({
    organizer: { label: 'Organizer', accessor: (c) => c?.organizer?.name || c?.organizerName || '', emptyLabel: 'No organizer' },
    status:    { label: 'Status',    accessor: (c) => c?.status || '',
      format: (v) => String(v).replace(/_/g,' ').replace(/^./, (x) => x.toUpperCase()) },
  }), []));
  const SORT_ACCESSORS = {
    organizer: (r) => r?.organizerName || r?.createdByName || '',
  };
  const sortedRows = sortRows(f.filterRows(conferences), SORT_ACCESSORS);
  // Paginate after sorting, so page 1 is the top of the sorted list
  // rather than the top of an arbitrary slice.
  const { pageRows, ...pager } = usePagination(sortedRows, 20);

  useEffect(() => {
    loadConferences();
  }, [searchTerm, statusFilter]);

  const loadConferences = async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchTerm,
        status: statusFilter || undefined
      };
      const data = await adminService?.getAllConferences(filters);
      setConferences(data);
    } catch (err) {
      setError(err?.message || 'Failed to load conferences');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (conferenceId, newStatus) => {
    try {
      await adminService?.updateConferenceStatus(conferenceId, newStatus);
      loadConferences();
    } catch (err) {
      setError(err?.message || 'Failed to update conference status');
    }
  };

  const handleDeleteConference = async (conferenceId) => {
    if (!window.confirm('Are you sure you want to delete this conference? This action cannot be undone.')) {
      return;
    }

    try {
      await adminService?.deleteConference(conferenceId);
      loadConferences();
    } catch (err) {
      setError(err?.message || 'Failed to delete conference');
    }
  };

  const formatDateSafe = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString) || 'Invalid date';
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-muted text-muted-foreground',
      published: 'bg-primary/10 text-primary',
      submission_open: 'bg-success/10 text-success',
      submission_closed: 'bg-warning/10 text-warning',
      review_phase: 'bg-secondary/10 text-secondary',
      completed: 'bg-muted text-muted-foreground',
      cancelled: 'bg-error/10 text-error'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground mb-4">Conference Management</h2>
        
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input
            placeholder="Search conferences..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            icon="Search"
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Any status"
            clearable
            className="min-w-[12rem]"
            options={conferenceStatusOptions()}
          />
        </div>

        {error && (
          <div className="mb-4 p-4 bg-error/10 border-2 border-error rounded-lg">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}
      </div>

      {loading ? (
        <TableSkeleton rows={6} columns={5} />
      ) : conferences?.length === 0 ? (
        <EmptyState
          icon="FolderOpen"
          title="No conferences yet"
          body="Conferences appear here once an organiser creates one on the platform."
        />
      ) : (
        <>
        <div className="overflow-x-auto">
          <table className="table-clear">
            <thead>
              <tr>
                <SortableTh field="name" sort={sort} onSort={toggle}>Conference</SortableTh>
                <SortableTh field="organizer" sort={sort} onSort={toggle} filter={f.headerProps('organizer')}>Organizer</SortableTh>
                <th>Dates</th>
                <SortableTh field="status" sort={sort} onSort={toggle} filter={f.headerProps('status')}>Status</SortableTh>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows?.map((conf) => (
                <tr key={conf?.id}>
                  <td>
                    <div>
                      <div className="font-medium text-foreground">{conf?.name}</div>
                      <div className="text-xs text-muted-foreground">{conf?.shortName}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="text-foreground">{conf?.organizerName}</div>
                      <div className="text-xs text-muted-foreground">{conf?.organizerEmail}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="text-foreground">{formatDate(conf?.startDate)}</div>
                      <div className="text-xs text-muted-foreground">to {formatDate(conf?.endDate)}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(conf?.status)}`}>
                      {conf?.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Select
                        value={conf?.status}
                        onChange={(value) => handleUpdateStatus(conf?.id, value)}
                        className="min-w-[11rem]"
                        options={conferenceStatusOptions()}
                      />
                      <button
                        onClick={() => handleDeleteConference(conf?.id)}
                        className="p-2 hover:bg-error/10 rounded-lg transition-colors"
                        title="Delete conference"
                      >
                        <Icon name="Trash2" size={18} className="text-error" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination {...pager} />
        </>
      )}
    </div>
  );
};

export default ConferenceManagement;